import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { ThemeContext } from 'styled-components';
import { FiCheck, FiDownload } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import { Bar } from 'react-chartjs-2';
import { addDays, endOfYear, format, startOfYear } from 'date-fns';
import { toast } from 'react-toastify';

import api from 'services/api';
import AppError from 'utils/AppError';
import { ChartContent, Container, Filtros } from './styles';
import { Emissora } from 'pages/@types';
import { graphOptions } from './ChartConfig';

import Card from 'components/Card';
import DatePicker from 'components/DatePicker';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import Switch from 'components/Switch';
import { Form } from '@unform/web';

interface IDadosAvaliacaoEng {
  label: string;
  aprovados: number;
  reprovados: number;
  ressalva: number;
}

interface EmissoraOption {
  id: number;
  label: string;
}

interface IGraficoAvaliacaoEng {
  options?: any;
  data: { datasets: any; labels?: any };
}

export default function IndicadorAvaliacaoEng() {
  const [loading, setLoading] = useState(true);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [ptoVenda, setPtoVenda] = useState<EmissoraOption[]>([]);
  const [todosPtoVenda, setTodosPtoVenda] = useState(true);
  const [totalAvaliados, setTotalAvaliados] = useState(0);
  const [initialData, setInitialData] = useState<{
    datainicio: Date;
    datafim: Date;
    politico: boolean;
    ptoVenda: string[];
  }>({
    datainicio: startOfYear(new Date()),
    datafim: endOfYear(new Date()),
    politico: true,
    ptoVenda: [],
  });
  const [graficoAvaliacaoEng, setGraficoAvaliacaoEng] = useState<
    IGraficoAvaliacaoEng
  >({
    data: { datasets: [] },
  });

  const formRef = useRef<FormHandles>(null);
  const themeContext = useContext(ThemeContext);

  const handleSelectedptoVenda = useCallback(() => {
    if (!formRef.current) return;

    if (formRef.current.getFieldValue('ptoVenda').length === 9)
      setTodosPtoVenda(true);
    else setTodosPtoVenda(false);
  }, []);

  const handleToogleptoVenda = useCallback(() => {
    if (!formRef.current) return;

    if (formRef.current.getFieldValue('ptoVenda').length === 9) {
      formRef.current.clearField('ptoVenda');
      setTodosPtoVenda(false);
    } else {
      formRef.current.setFieldValue(
        'ptoVenda',
        ptoVenda.map(p => p.id),
      );
      setTodosPtoVenda(true);
    }
  }, [ptoVenda]);

  const loadIndicadorAvaliacaoEng = useCallback(
    async data => {
      setLoading(true);
      try {
        const response = await api.get('/indicadores/avaliacaoeng', {
          params: {
            datainicio: format(data.datainicio, 'yyyy-MM-dd'),
            datafim: format(data.datafim, 'yyyy-MM-dd'),
            ptovenda: data.ptoVenda.join(),
            politico: data.politico,
          },
        });
        if (response && response.data) {
          const dados = response.data as IDadosAvaliacaoEng[];
          const labels = dados.map(d => d.label);
          const aprovado = dados.map(d => d.aprovados);
          const ressalva = dados.map(d => d.ressalva);
          const reprovado = dados.map(d => d.reprovados);

          setTotalAvaliados(
            dados.reduce(
              (total: number, d: IDadosAvaliacaoEng) =>
                total + d.aprovados + d.ressalva + d.reprovados,
              0,
            ),
          );

          setGraficoAvaliacaoEng({
            options: graphOptions(themeContext),
            data: {
              labels,
              datasets: [
                {
                  label: 'APROVADO',
                  backgroundColor: themeContext?.graph.aprovado,
                  borderWidth: 0,
                  data: aprovado,
                },
                {
                  label: 'RESSALVA',
                  backgroundColor: themeContext?.graph.ressalva,
                  borderWidth: 0,
                  data: ressalva,
                },
                {
                  label: 'REPROVADO',
                  backgroundColor: themeContext?.graph.reprovado,
                  borderWidth: 0,
                  data: reprovado,
                },
              ],
            },
          });
        } else {
          toast.error('Falha de comunicação com o sistema');
        }
      } catch (err) {
        if (err instanceof Error) {
          toast.error(err.message);
        } else {
          toast.error((err as AppError).message);
        }
      } finally {
        setLoading(false);
      }
    },
    [themeContext],
  );

  useEffect(() => {
    const controller = new AbortController();
    async function loadEmissoras() {
      try {
        const response = await api.get('/emissoras');
        if (response && response.data) {
          const options = response.data.map((emi: Emissora) => ({
            id: emi.ptoVenda,
            label: emi.ptoVenda,
          }));
          // Adicionando o ponto de venda Globo no meio das opções
          options.splice(4, 0, {
            id: 'GLOBO',
            label: 'GLOBO',
          });

          // Preenchendo opções de PtoVenda
          setPtoVenda(options);

          // Definindo valor inicial do filtro
          const ptoVendaIds = options.map(option => option.id);
          setInitialData(prevState => ({
            ...prevState,
            ptoVenda: ptoVendaIds,
          }));

          // Carregando o gráfico inicial
          loadIndicadorAvaliacaoEng({
            datainicio: startOfYear(new Date()),
            datafim: endOfYear(new Date()),
            politico: true,
            ptoVenda: ptoVendaIds,
          });
        } else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    }
    loadEmissoras();
    return () => {
      controller.abort();
    };
  }, [loadIndicadorAvaliacaoEng]);

  const download = useCallback(async (response, filename) => {
    const elink = document.createElement('a');
    const virtualUrl = window.URL.createObjectURL(response.data);
    elink.href = virtualUrl;
    elink.download = filename;
    elink.click();
  }, []);

  const handleDownloadRelatorioComerciais = useCallback(async () => {
    if (!formRef.current) return;

    const data = formRef.current.getData();
    setLoadingDownload(true);
    try {
      const response = await api.get('/indicadores/relatoriocomercial', {
        timeout: 360000,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
        params: {
          ptovenda: data.ptoVenda.join(),
          datainicio: format(data.datainicio, 'yyyy-MM-dd'),
          datafim: format(addDays(data.datafim, 1), 'yyyy-MM-dd'),
          filtrarPoliticos: data.politico,
        },
      });
      setLoadingDownload(false);

      await download(response, 'Relatório Comerciais ADX.xlsx');
    } catch (err) {
      console.log(err);
    }
  }, [download]);

  return (
    <Container>
      <header>
        <Form
          ref={formRef}
          onSubmit={loadIndicadorAvaliacaoEng}
          initialData={initialData}
        >
          <Filtros>
            <div>
              <fieldset>
                <legend>Período</legend>
                <DatePicker
                  placeholderText="DE"
                  name="datainicio"
                  disabled={loading}
                />
                <DatePicker
                  placeholderText="ATÉ"
                  name="datafim"
                  disabled={loading}
                />
              </fieldset>
              <fieldset>
                <legend>
                  Ponto de Venda
                  <Button
                    fill
                    color="primary"
                    size="sm"
                    onClick={handleToogleptoVenda}
                    type="button"
                  >
                    TODOS ? {todosPtoVenda ? 'SIM' : 'NÃO'}
                  </Button>
                </legend>
                <Checkbox
                  columns="1fr 1fr 1fr 1fr 1fr"
                  name="ptoVenda"
                  options={ptoVenda}
                  disabled={loading}
                  handleSelected={handleSelectedptoVenda}
                />
              </fieldset>
              <fieldset>
                <legend>Políticos</legend>
                <Switch name="politico" />
              </fieldset>
            </div>
            <Button size="sm" color="primary" disabled={loading}>
              BUSCAR
              <FiCheck />
            </Button>
          </Filtros>
        </Form>
      </header>
      <Card>
        <header className="headerGraph">
          <h4>Total Avaliado: {totalAvaliados.toLocaleString('pt-BR')}</h4>
          <h4>Gráfico de Avaliação Técnica de Comerciais</h4>
          <div>
            <Button
              color="primary"
              disabled={loadingDownload}
              onClick={handleDownloadRelatorioComerciais}
            >
              {loadingDownload ? (
                'Gerando o relatório...'
              ) : (
                <>
                  <FiDownload /> Gerar relatório
                </>
              )}
            </Button>
          </div>
        </header>
        <ChartContent>
          {loading ? (
            <p>Carregando gráfico...</p>
          ) : (
            <Bar
              data={graficoAvaliacaoEng.data}
              options={graficoAvaliacaoEng.options}
            />
          )}
        </ChartContent>
      </Card>
    </Container>
  );
}
